
import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { computed } from '@angular/core';
import { SaleInArea } from 'src/models/estate/IResidenceSaleInArea';
import { MarketFilterModel, StoredMarketFilterModel, TemporaryArea } from '@models/map/market-filter.model';
import { ILatLng } from 'polydraw';
import { SearchDropDownItem } from 'src/models/shared/filter.model';
import { CompareUtil } from 'src/util/tools/compare.util';

export const MAX_UNITS_IN_LIST = 500;

// Compares only the filter *criteria* of two API filter models. geoBorder/areas/sortOrder are
// excluded: areas and polygons are tracked separately (areasChanged/polygonChanged)
function filterCriteriaEquals(a: MarketFilterModel, b: MarketFilterModel): boolean {
    const criteria = (m: MarketFilterModel) => {
        if (!m) return {};
        const { geoBorder, areas, sortOrder, ...rest } = m as MarketFilterModel;
        return rest;
    };
    return JSON.stringify(criteria(a)) === JSON.stringify(criteria(b));
}

export type FavoriteListState = {
    name: string;
    id: number;
    createdDate: Date;
    sales: SaleInArea[];
    units: number[];
    originalAreas: TemporaryArea[];
    originalPolygon: ILatLng[][][];
    originalFilters: MarketFilterModel;
    areas: TemporaryArea[];
    polygon: ILatLng[][][];
    filters: MarketFilterModel;
    unitsChanged: boolean;
    areasChanged: boolean;
    polygonChanged: boolean;
    filtersChanged: boolean;
    changed: boolean;
    editName: boolean;
    sortOrder: SearchDropDownItem;
};

const initialState : FavoriteListState = {
    name: 'Liste uten tittel',
    id: -1, // same as filterId
    createdDate: new Date(),
    sales: [], // To be populated with data from new procedures
    units: [], // To be populated with data from new procedures
    originalAreas: [] as TemporaryArea[],
    originalPolygon: [] as ILatLng[][][],
    originalFilters: {} as MarketFilterModel,
    areas: [] as TemporaryArea[],
    polygon: [] as ILatLng[][][],
    filters: {} as MarketFilterModel,
    unitsChanged: false,
    areasChanged: false,
    polygonChanged: false,
    filtersChanged: false,
    changed: false,
    editName: false,
    sortOrder: null
};

// Used to show selected or ongoing new favorite list in the favorite list component.
export const SelectedFavoriteListsStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((state) => ({
        isFavoriteListEmpty: computed(() => !!state.sales()?.length),
        numberOfUnits: computed(() => state.units()?.length),
        content: computed(() => state.sales()),
        tooManyUnits: computed(() => state.units()?.length > MAX_UNITS_IN_LIST),
        canSave: computed(() => state.changed() && !(state.units()?.length > MAX_UNITS_IN_LIST))
    })),
    withMethods((store) => ({
        load(favoriteId: number, favorite: StoredMarketFilterModel, filters: MarketFilterModel): void {
            patchState(store, {
                ...initialState,
                id: favoriteId,
                name: favorite.name,
                polygon: favorite.geoBorder,
                areas: favorite.areas,
                units: favorite.unitIds,
                createdDate: new Date(),
                filters,
                originalAreas: [...favorite.areas],
                originalPolygon: [...favorite.geoBorder],
                originalFilters: { ...filters },
                sortOrder: store.sortOrder()
            });
        },
        addLazyLoadedSales(sales: SaleInArea[]) {
            patchState(store, { sales: [...sales] });
        },
        setId(id: number): void {
            patchState(store, { id });
        },
        updateSales(sales: SaleInArea[]): void {
            const uniqueUnitIds = Array.from(new Set([...sales.map(s => s.unitId)]));
            patchState(store, () => ({ sales: [...sales ], units: uniqueUnitIds, changed: true, unitsChanged: true }));
        },
        updateAreas(areas: TemporaryArea[]): void {
            const changedFromOriginal = JSON.stringify(areas) !== JSON.stringify(store.originalAreas());
            patchState(store, () => ({ areas: [...areas ] , changed: changedFromOriginal, areasChanged: changedFromOriginal } ));
        },
        updateName(name: string): void {
            patchState(store, { name, changed: true });
        },
        updateUnits(units: number[]): void {
            const uniqueList = Array.from(new Set([...units]));
            patchState(store, () => ({ units: [...uniqueList ] , changed: true, unitsChanged: true} ));
        },
        updatePolygon(polygon: ILatLng[][][]): void {
            const changedFromOriginal = !CompareUtil.polygonsEqual(polygon, store.originalPolygon());
            patchState(store, { polygon, changed: changedFromOriginal, polygonChanged: changedFromOriginal });
        },
        updateChanged(changed: boolean): void {
            patchState(store, { changed });
        },
        updateFilters(filters: MarketFilterModel): void {
            const filtersChanged = !filterCriteriaEquals(filters, store.originalFilters());
            patchState(store, (state) => ({
                filters,
                filtersChanged,
                changed: filtersChanged || state.unitsChanged || state.areasChanged || state.polygonChanged,
            }));
        },
        updateEditName(editName: boolean): void {
            patchState(store, { editName });
        },
        updateSortOrder(sortOrder: SearchDropDownItem): void {
            const patchedSort = {
                ...sortOrder, 
                key: (sortOrder.key as string).replace("address", "streetAddress")
            };
            sessionStorage.setItem('favouriteListCurrentSortItem', JSON.stringify(patchedSort));
            patchState(store, { sortOrder: patchedSort });
        },
        clear(): void {
            patchState(store, initialState);
        }
    }))
);